import { encodeUrlSafeBase64, buildInternalThreadUrl } from "../Conversations/threadUrlUtils.js";
import { getNotificationCategory } from "./components/NotificationPanel.jsx";

export const NOTIFICATION_MENU_QUERY_KEY = ["notificationMenu"];

// Contador del header, desplegable de la campana y panel del dashboard
export const invalidateNotificationQueries = (queryClient) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === NOTIFICATION_MENU_QUERY_KEY[0] ||
      (queryKey[0] === "getData" &&
        typeof queryKey[1] === "string" &&
        queryKey[1].startsWith("dashboard/notifications")),
  });

// @[Nombre](user:id) → @Nombre
const formatMessageContent = (content) => {
  if (!content) return content;
  const mentionRegex = /@\[([^\]]+)\]\(user:\d+\)/g;
  return content.replace(mentionRegex, (match, name) => `@${name.trim()}`);
};

const USER_ROLE_LABELS = {
  client: "cliente",
  consultant: "consultor",
  agency_admin: "administrador de agencia",
};

export const getNotificationSummary = (notification) => {
  if (!notification) return "Nueva actividad registrada.";

  if (notification.type === "user_created") {
    const userName = notification.data?.user_name?.trim();
    const translatedRole = USER_ROLE_LABELS[notification.data?.user_role];
    const roleText = translatedRole ? ` ${translatedRole}` : "";

    return userName
      ? `Se ha creado un nuevo usuario${roleText}: ${userName}.`
      : `Se ha creado un nuevo usuario${roleText}.`;
  }

  if (notification.type === "central_announcement") {
    const title =
      notification.data?.title ||
      notification.summary ||
      "Nuevo anuncio del sistema";
    return notification.data?.created_at ? ` Nuevo anuncio: ${title} ` : title;
  }

  if (notification.type === "unread_in_thread") {
    let summary = "";
    if (notification.summary) summary = formatMessageContent(notification.summary);
    else if (notification.data?.preview) summary = formatMessageContent(notification.data.preview);
    else summary = "Nuevo mensaje";

    return summary.replace(/\.$/, "");
  }

  if (notification.type === "conversation_mention") {
    if (notification.summary) {
      return formatMessageContent(notification.summary).replace(/\.$/, "");
    }
    if (notification.source === "ticket") {
      return "Te han mencionado en una conversación de un Ticket.";
    }
  }

  let summary = "";
  if (notification.summary)
    summary = formatMessageContent(notification.summary);
  else if (notification.data?.preview)
    summary = formatMessageContent(notification.data.preview);
  else if (notification.data?.ticket_title)
    summary = `Ticket: ${formatMessageContent(notification.data.ticket_title)}`;
  else if (notification.data?.task_title)
    summary = formatMessageContent(notification.data.task_title);
  else if (notification.data?.client_name)
    summary = notification.data.client_name;
  else if (notification.type === "report_available") {
    return notification.data?.message || "Tu informe ya está disponible.";
  } else if (notification.title)
    summary = formatMessageContent(notification.title);
  else summary = "Nueva actividad registrada.";

  return summary.replace(/\.$/, "");
};

const getNotificationClientId = (notification) =>
  notification.client_id ||
  notification.data?.client_id ||
  notification.client?.id ||
  notification.data?.client?.id ||
  null;

const getContentBasePath = (userRole) =>
  userRole === "client" ? "/contenido" : "/HerramientasIA/contenidos";

export const isContentNotification = (notification) =>
  notification.type === "content_review" || notification.type === "content_updated";

export const getContentNotificationRoute = (notification, userRole) => {
  const basePath = getContentBasePath(userRole);
  const query = new URLSearchParams();
  const clientId = getNotificationClientId(notification);
  const configId = notification.data?.config_id;
  const pieceId = notification.data?.content_piece_id;
  const groupId = notification.data?.group_id;
  const threadId = notification.thread_id || notification.data?.thread_id;

  if (threadId) {
    query.set("thread", String(threadId));
  }

  if (groupId) {
    // El listado de contenidos redirige a la página del grupo
    query.set("group", String(groupId));
    if (pieceId) {
      query.set("piece", String(pieceId));
    }
  } else if (pieceId) {
    query.set("piece", String(pieceId));
  }

  if (configId) {
    query.set("config", String(configId));
  }

  if (clientId) {
    query.set("client", String(clientId));
  }

  if (query.toString()) {
    query.set("local", "1");
    return `${basePath}?${query.toString()}`;
  }

  return basePath;
};

export const getNotificationRoute = (notification, userRole) => {
  if (!notification) return null;
  const type = notification.type;
  const category = getNotificationCategory(type);
  const clientId = getNotificationClientId(notification);
  const ticketId =
    notification.ticket_id ||
    notification.data?.ticket_id ||
    notification.data?.ticket?.id;
  const isSynchronizedThread = Boolean(notification.data?.is_thread);
  const synchronizedMessageId =
    notification.data?.is_thread_root_message_id ||
    notification.data?.last_message_id || null;
  const synchronizedThreadTitle = (notification.data?.is_thread_title || "").trim();

  if (type === "unread_in_thread" || type === "conversation_mention" || type === "conversation_important") {
    const isTicketThread =
      notification.thread_type === "ticket" ||
      notification.source === "ticket";

    const isClientThread =
      notification.thread_type === "client_portal" ||
      notification.source === "client_portal" ||
      notification.thread_type === "general";

    if (notification.thread_id && isTicketThread && ticketId) {
      return `/tickets/${ticketId}`;
    }

    if (notification.thread_id && notification.thread_type === "content_piece") {
      const query = new URLSearchParams({
        thread: String(notification.thread_id),
        local: "1",
      });
      if (clientId) {
        query.set("client", String(clientId));
      }
      return `${getContentBasePath(userRole)}?${query.toString()}`;
    }

    if (notification.thread_id && notification.thread_type === "internal") {
      return buildInternalThreadUrl(notification.thread_id, {
        messageThreadId: isSynchronizedThread ? synchronizedMessageId : null,
        messageThreadTitle: synchronizedThreadTitle,
      });
    }

    if (notification.thread_id && isClientThread && clientId) {
      if (!isSynchronizedThread || !synchronizedMessageId) {
        return `/conversations/${clientId}/${notification.thread_id}`;
      }

      const params = new URLSearchParams();
      params.set("mt", encodeUrlSafeBase64(synchronizedMessageId));
      if (synchronizedThreadTitle) {
        params.set("mtt", synchronizedThreadTitle);
      }

      return `/conversations/${clientId}/${notification.thread_id}?${params.toString()}`;
    }
  }

  if ((category === "ticket" || notification.thread_type === "ticket") && ticketId) {
    return `/tickets/${ticketId}`;
  }

  if (
    type === "client_info_updated" ||
    type === "client_onboarding_completed" ||
    type === "client_created"
  ) {
    if (clientId) {
      return `/clients?view=detail&clientId=${clientId}`;
    }
  }
  // Only client users receive it, so it goes to the client route.
  if (type === "report_available") {
    const reportId = notification.data?.report_id;
    return reportId ? `/informes/${reportId}` : "/informes";
  }
  return null;
};
